---
title: Privacy
updated: 2026-10-02
---

## 1. Titolare del Trattamento

Draughts Game ("noi") è il titolare del trattamento dei dati personali raccolti tramite questa piattaforma. Contatto: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)

## 2. Dati che Raccogliamo

### 2.1 Dati dell'Account

- **Nome utente** - scelto da te, visibile pubblicamente
- **Indirizzo email** - crittografato a riposo con AES-256-GCM, usato per l'identificazione dell'account, non visibile pubblicamente
- **Password** - memorizzata come hash bcrypt (fattore costo 12), mai in chiaro
- **Conferma eta** - registriamo che hai confermato di avere almeno 16 anni alla registrazione
- **Stato di verifica email** - se hai confermato il tuo indirizzo email, oltre a token temporanei di verifica e di reimpostazione password
- **Personalizzazione del profilo (opzionale)** - avatar e paese, scelti da te, visibili pubblicamente

### 2.2 Dati di Gioco

- Mosse, risultati e timestamp delle partite
- Rating e statistiche dei giocatori
- Partecipazione ai tornei e risultati
- Tentativi sui problemi, rating problemi e serie di soluzioni (streak)
- La tua libreria di studio: note di posizione (testo libero che scrivi tu), cartelle, partite salvate e posizioni salvate nell'editor — private, visibili solo a te

### 2.3 Dati Tecnici

- Indirizzo IP (per sicurezza e anti-cheating)
- Stringa user agent del browser
- Token di sessione

### 2.4 Richieste di Supporto

Se ci contatti tramite il modulo di supporto, raccogliamo il tuo indirizzo email, un nome facoltativo, l'oggetto e il contenuto del tuo messaggio, per poter gestire e rispondere alla tua richiesta.

## 3. Base Giuridica del Trattamento

Trattiamo i tuoi dati personali sulla base di:

- **Esecuzione del contratto** (Art. 6(1)(b) GDPR) - per fornire il servizio di gioco a cui ti sei registrato, incluse le email transazionali (verifica account, reimpostazione password) e le risposte alle richieste di supporto.
- **Interessi legittimi** (Art. 6(1)(f) GDPR) - per la sicurezza della piattaforma, anti-cheating, statistiche aggregate sul gioco (quali varianti si giocano, come finiscono le partite) e miglioramento del servizio.
- **Consenso** (Art. 6(1)(a) GDPR) - per la misura d'uso tramite Umami e il tracciamento errori tramite Sentry. Nessuno dei due parte finché non accetti dal banner.

## 4. Come Utilizziamo i Tuoi Dati

- Fornitura e gestione della piattaforma di gioco
- Autenticazione del tuo account e mantenimento delle sessioni
- Calcolo e visualizzazione dei rating e delle classifiche
- Matchmaking e organizzazione dei tornei
- Rilevamento e prevenzione di imbrogli e abusi
- Invio di email transazionali (verifica account, reimpostazione password)
- Gestione e risposta alle tue richieste di supporto
- Miglioramento del servizio tramite analisi aggregate e anonime
- Analisi dei dati di gioco (es. varianti giocate, risultati) in forma aggregata e anonimizzata per comprendere l'utilizzo e migliorare la piattaforma

## 5. Cookie e Servizi di Terze Parti

Utilizziamo cookie essenziali per l'autenticazione e le tue preferenze. Ci affidiamo ai seguenti servizi:

- **Umami** (analytics) - self-hosted sulla nostra infrastruttura nell'UE. Non usa cookie e parte solo se accetti le statistiche dal banner. Le visite sono identificate da un'impronta pseudonima, ricavata dal tuo indirizzo IP e dal browser e rinnovata ogni giorno; i numeri vengono usati solo in forma aggregata e non ti tracciamo su altri siti.
- **Sentry** (tracciamento errori) - attivato solo con il tuo consenso.
- **Resend** (invio email) - consegna le nostre email transazionali (verifica account, reimpostazione password) e le notifiche di supporto.
- **DigitalOcean** (hosting) - data center UE (Francoforte).

Non vendiamo i tuoi dati né utilizziamo tracker pubblicitari.

## 6. Conservazione dei Dati

- **Dati dell'account** - conservati finché il tuo account è attivo.
- **Account ospite** - eliminati automaticamente dopo 30 giorni di inattività.
- **Account non verificati** - se ti iscrivi ma non confermi mai il tuo indirizzo email, l'account viene eliminato automaticamente dopo 30 giorni di inattività.
- **Token di aggiornamento** - scadono dopo 7 giorni (90 per gli account ospite, così ritrovi le tue partite senza una password), puliti automaticamente ogni ora. L'indirizzo IP e i dati del browser legati a una sessione vengono cancellati dopo 7 giorni, anche quando la sessione dura di più.
- **Token di verifica email** - scadono dopo 24 ore.
- **Token di reimpostazione password** - scadono dopo 1 ora.
- **Richieste di supporto** - conservate per il tempo necessario a gestire la tua richiesta.
- **Segnalazioni di abuso** - quando un giocatore segnala qualcuno per abusi in chat, la chat di quella partita (fino a 100 messaggi, di entrambi i giocatori) viene conservata per la moderazione ed eliminata automaticamente dopo 90 giorni. Per il resto la chat non viene mai conservata.
- **Dati di gioco** - conservati a tempo indeterminato come parte del registro pubblico delle partite.
- **Libreria di studio** - note di posizione, cartelle, partite salvate e posizioni salvate sono conservate finché non le elimini tu, e comunque rimosse con la cancellazione dell'account.
- **Dati errori Sentry** - eliminati automaticamente dopo 90 giorni.
- **Account eliminati** - i dati personali vengono rimossi immediatamente alla cancellazione. I registri delle partite vengono anonimizzati.

## 7. I Tuoi Diritti (GDPR)

Ai sensi del Regolamento Generale sulla Protezione dei Dati (GDPR), hai il diritto di:

- **Accesso** (Art. 15) - scarica una copia dei tuoi dati personali dalle impostazioni del profilo ("Esporta i miei dati").
- **Rettifica** (Art. 16) - cambia la tua password nelle impostazioni del profilo. Per altre correzioni, contattaci.
- **Cancellazione** (Art. 17) - elimina il tuo account dalle impostazioni del profilo ("Elimina account"). L'operazione è immediata e irreversibile.
- **Portabilità** (Art. 20) - esporta i tuoi dati in formato JSON leggibile da macchina dalle impostazioni del profilo.
- **Limitazione** (Art. 18) - richiedi la limitazione del trattamento contattandoci.
- **Opposizione** (Art. 21) - rifiutando le statistiche dal banner dei cookie si spengono **sia** la misura d'uso Umami **sia** il tracciamento degli errori Sentry. Puoi ripensarci in qualsiasi momento dall'interruttore nelle impostazioni del profilo.

Per esercitare uno di questi diritti, usa gli strumenti self-service nelle impostazioni del profilo, oppure contattaci a [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com). Risponderemo entro 30 giorni.

## 8. Sicurezza dei Dati

Implementiamo misure tecniche e organizzative appropriate per proteggere i tuoi dati personali, tra cui crittografia dei dati sensibili a riposo e in transito, hashing sicuro delle password e rate limiting sugli endpoint di autenticazione.

## 9. Trasferimenti Internazionali

I tuoi dati sono principalmente archiviati e trattati nello Spazio Economico Europeo (SEE) nel data center di DigitalOcean a Francoforte. I dati degli errori Sentry possono essere trasferiti negli USA in base alla decisione di adeguatezza EU-US Data Privacy Framework (luglio 2023). Puoi disattivare Sentry nelle impostazioni del profilo. Le email transazionali vengono consegnate tramite Resend (USA); questo trasferimento è coperto da un accordo sul trattamento dei dati con Clausole Contrattuali Standard.

## 10. Privacy dei Minori

Il Servizio non è destinato a minori di 16 anni (Art. 8 GDPR). Richiediamo la conferma dell'età alla registrazione. Non raccogliamo consapevolmente dati personali di minori di 16 anni. Se scopriamo di aver raccolto dati di un minore di 16 anni, li cancelleremo prontamente.

## 11. Modifiche a Questa Policy

Potremmo aggiornare questa Privacy Policy di tanto in tanto. Notificheremo gli utenti registrati di modifiche sostanziali tramite un avviso sulla piattaforma. La data "Ultimo aggiornamento" in alto indica la revisione più recente.

## 12. Autorità di Controllo

Hai il diritto di presentare un reclamo all'autorità di controllo competente per la protezione dei dati se ritieni che i tuoi diritti sulla protezione dei dati siano stati violati.

## 13. Contatti

Per domande su questa Privacy Policy:

- Email: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)
